"use client";

import API, { AssetDownload, type DownloadHistorySortBy } from "@/api/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDownloadHistory } from "@/hooks/use-assets";
import { cn, formatFileSize, formatRelativeTime } from "@/lib/utils";
import { Download, Info } from "lucide-react";
import React from "react";

import { DownloadMetadataDrawer } from "./download-metadata-drawer";
import { Pager } from "./pager";
import { SortableHead, type SortState, toggleSort } from "./sortable-head";

const api = new API();
const PAGE_SIZE = 50;

export function DownloadHistoryView() {
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [exportAll, setExportAll] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<SortState<DownloadHistorySortBy>>({
    column: "download_initiated_at",
    direction: "desc",
  });

  const downloadHistoryQuery = useDownloadHistory({
    page,
    page_size: PAGE_SIZE,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    sort_by: sort.column,
    sort_dir: sort.direction,
  });
  const downloadHistory = downloadHistoryQuery.data?.downloads ?? [];
  const total = downloadHistoryQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isLoading = downloadHistoryQuery.isLoading;

  const [selectedDownload, setSelectedDownload] = React.useState<AssetDownload | null>(null);
  const [metadataDrawerOpen, setMetadataDrawerOpen] = React.useState(false);

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await api.exportDownloadHistory({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        export_all: exportAll,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      relative: formatRelativeTime(date),
      absolute: date.toLocaleString(navigator.language, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }),
    };
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default">Completed</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "initiated":
        return <Badge variant="secondary">Initiated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleOpenMetadata = (download: AssetDownload) => {
    setSelectedDownload(download);
    setMetadataDrawerOpen(true);
  };

  const handleSort = (column: DownloadHistorySortBy) => {
    setSort((prev) => toggleSort(prev, column));
    setPage(1);
  };
  const handleStartDate = (value: string) => {
    setStartDate(value);
    setPage(1);
  };
  const handleEndDate = (value: string) => {
    setEndDate(value);
    setPage(1);
  };

  const exportDisabled = isLoading || isExporting;

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold">Download History</h1>
          <p className="text-muted-foreground">View asset download activity and audit logs</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="start-date" className="whitespace-nowrap text-sm">
              From
            </Label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => handleStartDate(e.target.value)}
              className={cn(
                "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="end-date" className="whitespace-nowrap text-sm">
              To
            </Label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => handleEndDate(e.target.value)}
              className={cn(
                "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="export-all"
              checked={exportAll}
              onCheckedChange={(checked) => setExportAll(checked === true)}
            />
            <Label htmlFor="export-all" className="cursor-pointer whitespace-nowrap text-sm">
              Export all records
            </Label>
          </div>
          <Button variant="outline" onClick={handleExportCsv} disabled={exportDisabled}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Download History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Downloads</CardTitle>
          <CardDescription>Complete audit trail of asset downloads</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset Name</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>User</TableHead>
                  <SortableHead column="organization_name" label="Organization" sort={sort} onSort={handleSort} />
                  <SortableHead column="download_initiated_at" label="Downloaded" sort={sort} onSort={handleSort} />
                  <TableHead>Size</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {downloadHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      No download history found.
                    </TableCell>
                  </TableRow>
                ) : (
                  downloadHistory.map((download) => (
                    <TableRow key={download.id}>
                      <TableCell className="font-medium">{download.asset_name}</TableCell>
                      <TableCell>{download.asset_version || "N/A"}</TableCell>
                      <TableCell>{download.user_email}</TableCell>
                      <TableCell>{download.organization_name}</TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{formatDate(download.download_initiated_at).relative}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{formatDate(download.download_initiated_at).absolute}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatFileSize(download.file_size_bytes)}</TableCell>
                      <TableCell>{formatDuration(download.download_duration_ms)}</TableCell>
                      <TableCell>{getStatusBadge(download.download_status)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenMetadata(download)}
                          title="View metadata"
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
          {total > 0 && (
            <Pager
              page={page}
              pageCount={pageCount}
              onPage={setPage}
              label="download history"
              summary={`Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} downloads`}
            />
          )}
        </CardContent>
      </Card>

      {/* Metadata Drawer */}
      <DownloadMetadataDrawer
        download={selectedDownload}
        open={metadataDrawerOpen}
        onOpenChange={setMetadataDrawerOpen}
      />
    </div>
  );
}
